import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLButtonElement} resetButton
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
  #controller = new AbortController();
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  #initialMarkup = '';
  #emptyMarkup = '';
  #recentSignature = '';
  /** @type {Map<string, {html: string, expires: number}>} */
  #cache = new Map();
  #revealFrame = 0;
  #keyboardInput = true;
  #resolvedQuery = '';

  get dialog() { return this.closest('dialog-component'); }

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#initialMarkup = this.refs.predictiveSearchResults.innerHTML;
    this.#emptyMarkup = this.#initialMarkup;
    const { signal } = this.#controller;
    this.dialog?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    this.dialog?.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
    this.addEventListener('pointerover', this.#loadHoverPhoto, { signal, passive: true });
    this.addEventListener('focusin', this.#loadHoverPhoto, { signal });
    this.addEventListener('pointerdown', () => { this.#keyboardInput = false; }, { signal, passive: true });
    this.refs.searchInput.addEventListener('compositionend', this.search, { signal });
    this.#syncInputState();
  }

  disconnectedCallback() {
    this.#cancelPending();
    cancelAnimationFrame(this.#revealFrame);
    this.#controller.abort();
    this.#cache.clear();
    super.disconnectedCallback();
  }

  #handleKeyboardShortcut = (event) => {
    this.#keyboardInput = true;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogOpen = () => {
    this.refs.searchInput.focus({ preventScroll: true });
    this.#reveal();
    if (this.refs.searchInput.value.trim()) this.search();
    else void this.#loadEmptyState();
  };

  #handleDialogClose = () => {
    this.#cancelPending();
    this.#clearSelection();
    if (this.dataset.remember !== 'true') {
      this.refs.searchInput.value = '';
      this.#render(this.#emptyMarkup);
      this.#syncInputState();
    }
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
  };

  #syncInputState() {
    const hasQuery = Boolean(this.refs.searchInput.value.trim());
    this.dataset.hasQuery = String(hasQuery);
    this.refs.resetButton.hidden = !hasQuery;
  }

  #cancelPending() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#resolvedQuery = '';
    this.#setStatus('');
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
  }

  #clearSelection() {
    this.querySelectorAll('[aria-selected="true"]').forEach((item) => {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    });
    this.refs.searchInput.removeAttribute('aria-activedescendant');
  }

  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.refs.predictiveSearchResults.querySelectorAll(
      'li.predictive-search-results__card, li.predictive-search-results__card--query, slideshow-slide.predictive-search-results__card'
    )].filter((item) => item.querySelector('a[href]') && !item.closest('.removing')));
  }

  /** Native Tab navigation is preserved; only input arrows and Enter are intercepted. */
  onSearchKeyDown = (event) => {
    if (event.target !== this.refs.searchInput || event.isComposing || event.defaultPrevented) return;
    const items = this.#items;
    const index = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!items.length || this.#timer || this.refs.predictiveSearchResults.hasAttribute('aria-busy')) return;
      this.#keyboardInput = true;
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? (index + 1) % items.length : (index <= 0 ? items.length - 1 : index - 1);
      this.#clearSelection();
      const item = items[next];
      if (!item) return;
      item.id ||= 'search-window-option-' + next;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'true');
      item.classList.add('keyboard-focus');
      this.refs.searchInput.setAttribute('aria-activedescendant', item.id);
      this.refs.searchInput.setAttribute('aria-expanded', 'true');
      item.scrollIntoView({ block: 'nearest', behavior: 'instant' });
      this.#loadHoverPhoto({ target: item, type: 'focusin' });
      return;
    }
    if (event.key !== 'Enter') return;
    if (!this.refs.searchInput.value.trim() && index < 0) {
      event.preventDefault();
      return;
    }
    // Never open stale results while a newer request or debounce is pending.
    if (this.#timer || this.#activeFetch || this.dataset.live === 'false') return;
    let link = index >= 0 ? items[index]?.querySelector('a[href]') : null;
    const hasCurrentResults = this.#resolvedQuery === this.refs.searchInput.value.trim() && this.#resolvedQuery !== '';
    if (!link && hasCurrentResults && this.dataset.enter === 'first') {
      link = this.querySelector('.predictive-search-results__card--product a[href]') || items[0]?.querySelector('a[href]');
    }
    if (!link && hasCurrentResults && (this.dataset.enter || 'single') === 'single') {
      const url = this.querySelector('[data-single-result-url]')?.getAttribute('data-single-result-url');
      if (url) link = items.map((item) => item.querySelector('a[href]')).find((anchor) => anchor?.getAttribute('href') === url);
    }
    if (link instanceof HTMLAnchorElement) {
      event.preventDefault();
      link.click(); // Preserve product-card navigation and its photo transition.
    }
    // Otherwise the original GET form handles all-results navigation and locale.
  };

  resetSearch = () => {
    this.#cancelPending();
    this.refs.searchInput.value = '';
    this.#syncInputState();
    this.#clearSelection();
    this.#render(this.#emptyMarkup);
    this.refs.searchInput.focus({ preventScroll: true });
    void this.#loadEmptyState();
  };

  search = (event) => {
    this.#cancelPending(); // Abort immediately, before the next debounce.
    this.#clearSelection();
    this.#syncInputState();
    const term = this.refs.searchInput.value.trim();
    if (!term) {
      this.#render(this.#emptyMarkup);
      void this.#loadEmptyState();
      return;
    }
    if (event?.isComposing || this.dataset.live === 'false' || term.length < Number(this.dataset.minChars || 1)) {
      this.#render(this.#emptyMarkup);
      return;
    }
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      void this.#getSearchResults(term);
    }, Number(this.dataset.delay || 200));
  };

  #setStatus(text) {
    const status = this.querySelector('.search-window__status');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = text;
    status.hidden = !text;
  }

  /** A bounded, short-lived HTML cache; requests are locale-aware and abortable. */
  async #fetchMarkup(url, signal) {
    if (!this.dataset.sectionId) throw new Error('Missing search section');
    url.searchParams.set('section_id', this.dataset.sectionId);
    const current = new URL(location.href);
    for (const key of ['preview_theme_id']) {
      const value = current.searchParams.get(key);
      if (value) url.searchParams.set(key, value);
    }
    const key = url.toString();
    const cached = this.#cache.get(key);
    if (!window.Shopify?.designMode && cached && cached.expires > Date.now()) return cached.html;
    const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Search response ' + response.status);
    const html = await response.text();
    if (!new DOMParser().parseFromString(html, 'text/html').getElementById('predictive-search-results')) {
      throw new Error('Search response is missing its results');
    }
    if (!signal.aborted && !window.Shopify?.designMode) {
      this.#cache.delete(key);
      if (this.#cache.size >= 12) this.#cache.delete(this.#cache.keys().next().value);
      this.#cache.set(key, { html, expires: Date.now() + 60000 });
    }
    return html;
  }

  #createRequest() {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    this.refs.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.#setStatus(this.dataset.loadingText || '');
    return controller;
  }

  async #getSearchResults(term) {
    const controller = this.#createRequest();
    const timeout = setTimeout(() => controller.abort('timeout'), 12000);
    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(new SearchUpdateEvent({ search: { query: term }, promise: deferred.promise }));
    try {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', this.dataset.types || 'product,collection,page,article,query');
      url.searchParams.set('resources[limit]', this.dataset.limit || '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      if (this.dataset.unavailable && this.dataset.unavailable !== 'default') {
        url.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailable);
      }
      const html = await this.#fetchMarkup(url, controller.signal);
      if (controller.signal.aborted || !this.isConnected || this.refs.searchInput.value.trim() !== term) {
        deferred.resolve({ totalCount: 0 });
        return;
      }
      this.#render(html);
      this.#resolvedQuery = term;
      this.#setStatus('');
      deferred.resolve({ totalCount: this.#items.length });
    } catch (error) {
      // Optional suggestions must never block normal search or reject without a handler.
      deferred.resolve({ totalCount: 0 });
      if (this.#activeFetch === controller && (!controller.signal.aborted || controller.signal.reason === 'timeout')) {
        this.#render(this.#emptyMarkup);
        this.#setStatus(this.dataset.errorText || '');
      }
    } finally {
      clearTimeout(timeout);
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
        this.refs.predictiveSearchResults.removeAttribute('aria-busy');
      }
    }
  }

  async #loadEmptyState() {
    if (this.dataset.recent === 'false' || this.refs.searchInput.value.trim()) return;
    const ids = RecentlyViewed.getProducts().slice(0, Number(this.dataset.recentCount || 4));
    const signature = ids.join(',');
    if (!ids.length || signature === this.#recentSignature) return;
    const controller = this.#createRequest();
    const timeout = setTimeout(() => controller.abort('timeout'), 12000);
    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', ids.map((id) => 'id:' + id).join(' OR '));
      url.searchParams.set('type', 'product');
      const html = await this.#fetchMarkup(url, controller.signal);
      if (controller.signal.aborted || !this.isConnected || this.refs.searchInput.value.trim()) return;
      const recent = new DOMParser().parseFromString(html, 'text/html').querySelector('.recently-viewed-wrapper');
      const empty = new DOMParser().parseFromString(this.#initialMarkup, 'text/html');
      const inner = empty.querySelector('.predictive-search-results__inner');
      if (recent && inner) {
        const group = empty.createElement('div');
        group.className = 'predictive-search-results__products';
        group.append(recent);
        inner.prepend(group);
        this.#emptyMarkup = empty.body.innerHTML;
        this.#render(this.#emptyMarkup);
      }
      this.#recentSignature = signature;
    } catch (error) {
      // Keep the server-rendered suggestions visible; retry on the next open.
    } finally {
      clearTimeout(timeout);
      if (this.#activeFetch === controller) {
        this.#activeFetch = null;
        this.refs.predictiveSearchResults.removeAttribute('aria-busy');
        this.#setStatus('');
      }
    }
  }

  #render(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const results = parsed.getElementById('predictive-search-results');
    if (!results) return;
    const holder = document.createElement('div');
    holder.append(results);
    morph(this.refs.predictiveSearchResults, holder);
    this.#clearSelection();
    const items = this.#items;
    items.forEach((item, index) => {
      item.id ||= 'search-window-option-' + index;
      item.setAttribute('role', 'option');
    });
    this.refs.searchInput.setAttribute('aria-expanded', String(items.length > 0));
    this.refs.predictiveSearchResults.scrollTop = 0;
    this.#reveal();
  }

  #reveal() {
    const content = this.refs.predictiveSearchResults;
    content.removeAttribute('data-reveal');
    cancelAnimationFrame(this.#revealFrame);
    this.#revealFrame = requestAnimationFrame(() => {
      this.#revealFrame = requestAnimationFrame(() => {
        if (this.isConnected) content.setAttribute('data-reveal', '');
      });
    });
  }

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();
    this.#recentSignature = '';
    this.#emptyMarkup = this.#initialMarkup;
    this.#cancelPending();
    const wrapper = this.querySelector('.recently-viewed-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    wrapper.classList.add('removing');
    void onAnimationEnd(wrapper, () => {
      wrapper.remove();
      this.#clearSelection();
    });
  }

  #loadHoverPhoto = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.type === 'pointerover' && !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (event.type === 'focusin' && !this.#keyboardInput) return;
    if (navigator.connection?.saveData) return;
    const card = event.target.closest('.resource-card') || event.target.querySelector('.resource-card');
    const image = card?.querySelector('img[data-search-src]');
    if (!(image instanceof HTMLImageElement) || image.dataset.loadingPhoto) return;
    image.dataset.loadingPhoto = 'true';
    image.sizes = Math.ceil(card.querySelector('.resource-card__image')?.clientWidth || 200) + 'px';
    image.srcset = image.dataset.searchSrcset || '';
    image.src = image.dataset.searchSrc || '';
    const ready = () => {
      if (image.isConnected && image.naturalWidth > 1) card.setAttribute('data-secondary-ready', '');
    };
    if (typeof image.decode === 'function') image.decode().then(ready).catch(() => { delete image.dataset.loadingPhoto; });
    else {
      image.addEventListener('load', ready, { once: true });
      image.addEventListener('error', () => { delete image.dataset.loadingPhoto; }, { once: true });
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
